import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
  IconButton,
  Stack,
  alpha,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { MdEdit, MdSearch, MdRefresh, MdSettings, MdCheckCircle, MdCancel } from "react-icons/md";
import axios from "axios";

const medstownColors = {
  primary: "#014D4D",
  secondary: "#02A0A0",
  accent: "#FFB800",
  success: "#4caf50",
  error: "#f44336",
  warning: "#ff9800",
  white: "#fff",
  bg: "#f6fbfb",
};

const typeChipColor = {
  boolean: { bg: alpha(medstownColors.warning, 0.15), fg: "#a36b00" },
  number: { bg: alpha(medstownColors.secondary, 0.15), fg: medstownColors.primary },
  string: { bg: alpha(medstownColors.primary, 0.1), fg: medstownColors.primary },
};

const AdminController = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [openModal, setOpenModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ name: "", value: "" });
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchData(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const fetchData = async (page) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `https://api.medstown.com/api/admin-controls?page=${page}`
      );
      if (response.status === 200) {
        setData(response.data.data.list || []);
        setTotalPages(response.data.data.totalPages || 1);
      }
    } catch (err) {
      setError("Failed to load data. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setFormData({
      name: item.name ?? "",
      value: item.value ?? "",
    });
    setError(null);
    setOpenModal(true);
  };

  const handleClose = () => {
    if (saving) return;
    setOpenModal(false);
    setSelectedItem(null);
  };

  const renderValueInput = () => {
    if (!selectedItem) return null;
    const { value_type, enum_values } = selectedItem;

    if (enum_values && enum_values.length > 0) {
      return (
        <FormControl fullWidth margin="dense" size="small">
          <InputLabel>Select Value</InputLabel>
          <Select
            label="Select Value"
            value={formData.value ?? ""}
            onChange={(e) => setFormData((p) => ({ ...p, value: e.target.value }))}
          >
            {enum_values.map((val, idx) => (
              <MenuItem key={idx} value={String(val)}>
                {String(val)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (value_type === "boolean") {
      const checked = formData.value === "true" || formData.value === true;
      return (
        <Paper
          variant="outlined"
          sx={{ p: 1.5, mt: 1, borderRadius: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <Typography variant="body2" fontWeight={600}>
            Value
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={checked}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, value: e.target.checked ? "true" : "false" }))
                }
                color="success"
              />
            }
            label={checked ? "True" : "False"}
            labelPlacement="start"
          />
        </Paper>
      );
    }

    if (value_type === "number") {
      return (
        <TextField
          fullWidth
          margin="dense"
          label="Value"
          type="number"
          size="small"
          value={formData.value}
          onChange={(e) => setFormData((p) => ({ ...p, value: e.target.value }))}
        />
      );
    }

    return (
      <TextField
        fullWidth
        margin="dense"
        label="Value"
        size="small"
        value={formData.value}
        onChange={(e) => setFormData((p) => ({ ...p, value: e.target.value }))}
      />
    );
  };

  const validate = () => {
    if (!selectedItem) return "No item selected";
    const { value_type, enum_values } = selectedItem;
    const v = formData.value;
    if (enum_values && enum_values.length > 0) {
      if (!enum_values.map(String).includes(String(v))) return "Choose a valid option";
      return null;
    }
    if (value_type === "number") {
      if (v === "" || v === null || isNaN(Number(v))) return "Enter a valid number";
    }
    if (value_type === "boolean") {
      if (v !== "true" && v !== "false") return "Toggle the value";
    }
    return null;
  };

  const handleUpdate = async () => {
    if (!selectedItem) return;
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const response = await axios.put(
        `https://api.medstown.com/api/admin-controls/${selectedItem.id}`,
        {
          name: formData.name,
          value: String(formData.value),
        }
      );
      if (response.status === 200) {
        await fetchData(currentPage);
        setOpenModal(false);
        setSelectedItem(null);
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to update the item");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const displayValue = (item) => {
    if (item.value_type === "boolean") {
      const v = item.value === "true" || item.value === true;
      return (
        <Chip
          size="small"
          icon={v ? <MdCheckCircle /> : <MdCancel />}
          label={v ? "True" : "False"}
          sx={{
            bgcolor: v ? alpha(medstownColors.success, 0.12) : alpha(medstownColors.error, 0.1),
            color: v ? medstownColors.success : medstownColors.error,
            fontWeight: 700,
            "& .MuiChip-icon": { color: "inherit" },
          }}
        />
      );
    }
    const text = item.value === "" || item.value == null ? "—" : String(item.value);
    return (
      <Typography
        variant="body2"
        fontWeight={700}
        sx={{
          color: "#000",
          textTransform: item.value_type === "string" ? "none" : "uppercase",
          wordBreak: "break-word",
        }}
      >
        {text}
      </Typography>
    );
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter(
      (d) =>
        d.name?.toLowerCase().includes(q) ||
        String(d.value ?? "").toLowerCase().includes(q) ||
        d.id?.toLowerCase().includes(q)
    );
  }, [data, search]);

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, bgcolor: medstownColors.bg, minHeight: "100vh" }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          background: `linear-gradient(135deg, ${medstownColors.primary}, ${medstownColors.secondary})`,
          color: medstownColors.white,
          boxShadow: "0 8px 24px rgba(1,77,77,0.25)",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ bgcolor: alpha("#fff", 0.18), borderRadius: 2, p: 1.2, display: "flex" }}>
              <MdSettings size={28} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: "-0.02em" }}>
                Admin Controls
              </Typography>
              {!isMobile && (
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  Manage system parameters, toggles and feature flags
                </Typography>
              )}
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={`${data.length} item${data.length === 1 ? "" : "s"}`}
              sx={{ bgcolor: alpha("#fff", 0.18), color: "#fff", fontWeight: 600 }}
              size="small"
            />
            <Tooltip title="Refresh">
              <IconButton
                onClick={() => fetchData(currentPage)}
                sx={{ color: "#fff", bgcolor: alpha("#fff", 0.12), "&:hover": { bgcolor: alpha("#fff", 0.22) } }}
                size="small"
              >
                <MdRefresh />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      {/* Search */}
      <Paper sx={{ p: 1.5, mb: 2, borderRadius: 2 }} elevation={0} variant="outlined">
        <TextField
          fullWidth
          size="small"
          placeholder="Search by name, value, or id…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <MdSearch />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {error && !openModal && (
        <Paper
          sx={{
            p: 1.5,
            mb: 2,
            borderRadius: 2,
            bgcolor: alpha(medstownColors.error, 0.08),
            color: medstownColors.error,
          }}
          variant="outlined"
        >
          <Typography variant="body2">{error}</Typography>
        </Paper>
      )}

      {/* List */}
      {loading && data.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : filtered.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: "center", borderRadius: 2 }} variant="outlined">
          <Typography color="text.secondary">No controls found.</Typography>
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
          {/* Column header (desktop only) */}
          {!isMobile && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 180px 110px",
                gap: 2,
                px: 2,
                py: 1.25,
                bgcolor: alpha(medstownColors.primary, 0.06),
                borderBottom: `1px solid ${alpha(medstownColors.primary, 0.08)}`,
              }}
            >
              <Typography variant="caption" fontWeight={700} sx={{ color: medstownColors.primary, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Function
              </Typography>
              <Typography variant="caption" fontWeight={700} sx={{ color: medstownColors.primary, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Value
              </Typography>
              <Typography variant="caption" fontWeight={700} sx={{ color: medstownColors.primary, textTransform: "uppercase", letterSpacing: "0.05em", textAlign: "right" }}>
                Action
              </Typography>
            </Box>
          )}

          {filtered.map((item, idx) => {
            const typeStyle = typeChipColor[item.value_type] || typeChipColor.string;
            const isLast = idx === filtered.length - 1;
            return (
              <Box
                key={item.id}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 180px 110px" },
                  gap: { xs: 1, md: 2 },
                  alignItems: "center",
                  px: 2,
                  py: 1.5,
                  bgcolor: "#fff",
                  borderBottom: isLast ? "none" : `1px solid ${alpha(medstownColors.primary, 0.06)}`,
                  transition: "background-color 0.15s ease",
                  "&:hover": { bgcolor: alpha(medstownColors.primary, 0.04) },
                }}
              >
                {/* Function column */}
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      sx={{ color: "#000", textTransform: "uppercase", letterSpacing: "0.02em" }}
                    >
                      {item.name}
                    </Typography>
                    <Chip
                      size="small"
                      label={item.value_type}
                      sx={{
                        bgcolor: typeStyle.bg,
                        color: typeStyle.fg,
                        fontWeight: 600,
                        fontSize: "0.65rem",
                        height: 18,
                      }}
                    />
                  </Stack>
                  {item.enum_values && item.enum_values.length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                      Options: {item.enum_values.join(", ")}
                    </Typography>
                  )}
                </Box>

                {/* Value column */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  {isMobile && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Value:
                    </Typography>
                  )}
                  {displayValue(item)}
                </Box>

                {/* Action column */}
                <Box sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" } }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<MdEdit />}
                    onClick={() => handleEdit(item)}
                    sx={{
                      color: medstownColors.primary,
                      borderColor: alpha(medstownColors.primary, 0.3),
                      fontWeight: 600,
                      textTransform: "none",
                      "&:hover": {
                        borderColor: medstownColors.primary,
                        bgcolor: alpha(medstownColors.primary, 0.06),
                      },
                    }}
                  >
                    Edit
                  </Button>
                </Box>
              </Box>
            );
          })}
        </Paper>
      )}

      {totalPages > 1 && (
        <Stack direction="row" justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, p) => setCurrentPage(p)}
            color="primary"
            size={isMobile ? "small" : "medium"}
          />
        </Stack>
      )}

      {/* Edit Dialog */}
      <Dialog open={openModal} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${medstownColors.primary}, ${medstownColors.secondary})`,
            color: "#fff",
            fontWeight: 700,
            py: 1.5,
          }}
        >
          Edit Control
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            margin="dense"
            label="Name"
            fullWidth
            value={formData.name}
            disabled
            size="small"
          />
          {renderValueInput()}

          {selectedItem && (
            <Box sx={{ mt: 2 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={`Type: ${selectedItem.value_type}`} variant="outlined" />
                {selectedItem.enum_values && selectedItem.enum_values.length > 0 && (
                  <Chip
                    size="small"
                    label={`Options: ${selectedItem.enum_values.join(", ")}`}
                    variant="outlined"
                  />
                )}
              </Stack>
            </Box>
          )}

          {error && (
            <Typography color="error" variant="caption" sx={{ mt: 1, display: "block" }}>
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdate}
            variant="contained"
            disabled={saving}
            startIcon={saving && <CircularProgress size={14} sx={{ color: "#fff" }} />}
            sx={{
              background: `linear-gradient(135deg, ${medstownColors.primary}, ${medstownColors.secondary})`,
              fontWeight: 700,
              textTransform: "none",
              px: 3,
            }}
          >
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminController;
